import React, { useEffect, useReducer, useMemo } from "react";
import { useOrders } from "../util/OrderContext";
import { DialogOrderTypes, IProcessedOrders } from "./constants";
import { OrderActionBar } from "./OrderActionButtons/OrderActionBar";
import { OrderListView } from "./OrderList/OrderListView";
import { setCollectionPoints, setOrders } from "./state/OrderParentActions";
import { initalState, OrderParentReducer, OrderTabs } from "./state/OrderParentReducer";
import { columns as newOrderColumn } from './Columns/NewOrders.columns'
import { sortFilterItems } from "../menu/products/Filters";
import { useSideTabs } from "../../hooks/useSideTabs";
import { useOrdersFilters } from "./hooks/useOrderFilters";
import { OrderListHeaders } from "./OrderList/OrderListViewHeaders";
import Styles from "../../scss/orderHistory.module.scss";
import detailsStyles from "../../scss/orderDetails.module.scss"
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../reducers";
import { PickupListContainer } from "./PickupListContainer";
import { useAllocateOrders } from "./hooks/useAllocateOrders";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { ICollectionPoint } from "../ordersSetting/Forms/CollectionPointEntity";
import { usePickupNow } from "./hooks/usePickupNow";
import { DisableRouteComponent } from "../util/DisableRouteComponent";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { OrderDetailsDialog } from "./Dialogs/OrderDetailsDialog";
import { MultiDialogRoute } from "../dialogRoutes/MultiDialogRoute";
import { useTranslation } from "react-i18next";
import { IOrderRouteChildren } from "./OrderRouter";
import { BY_ID, dialogKeys, REFUND_REASONS_URL, SPECIFIC_ITEMS_REFUND_URL } from "../../const/URL.const";
import { IssueRefundDialog } from "./PartialRefund/IssueRefundDialog";
import { OrderDetailsChecklistDialog } from "./PartialRefund/OrderDetailsChecklistDialog";
import { useDialogRouteStages } from "../dialogRoutes/useDialogRouteStages";
import { generateTemplateURL } from "../../services/generateConsumerURLs";


interface IOrderParentContainerProps extends IOrderRouteChildren {
    orderKey: OrderKeys;
}

export type OrderKeys = "openedOrders" | "newOrders";

export function OrderParentContainer({ orderKey, tab }: IOrderParentContainerProps) {
    const [state, dispatch] = useReducer(OrderParentReducer, initalState);
    const { dineIn, delivery, collection, fullList, selectedTab, filterState, filterCriteria, collectionPoints } = state;
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const venueSettings = useSelector((state: IReduxAppState) => state.venue[selectedVenueId].venueSettings);
    const { apiData } = useAPIV2<ICollectionPoint[]>(APIExtensions.collectionPoints, { yoelloAPIParams: { venue_id: selectedVenueId, include_disabled: true } });
    const position = useSideTabs(tab);
    const { [orderKey]: orderList, setMessageOptions } = useOrders()!;
    const { t } = useTranslation();
    const history = useHistory();
    const { goToNextStep } = useDialogRouteStages(history);
    useOrdersFilters(dispatch, collectionPoints!);
    useAllocateOrders(collectionPoints!, !venueSettings?.pickup_now_enabled);

    const isPickupNow = usePickupNow()
    let { path } = useRouteMatch();

    const orderItemList = {
        dineIn, delivery, collection, fullList
    }
    useEffect(() => {
        if (!apiData) return;
        dispatch(setCollectionPoints(apiData));
    }, [apiData]);

    const selectedList = useMemo(() => {
        if (selectedTab === OrderTabs.ALL) {
            return fullList;
        }
        //@ts-ignore
        const items = state[selectedTab];
        if (filterState[selectedTab] && filterCriteria[selectedTab]) {
            return sortFilterItems(filterState[selectedTab], filterCriteria[selectedTab], items)
        }
        return items;
        //eslint-disable-next-line
    }, [selectedTab, filterCriteria, dineIn, delivery, collection, fullList])

    useEffect(() => {
        if (orderList) {
            dispatch(setOrders(orderList as IProcessedOrders[]));
        }
    }, [orderList]);
    function openOrderDialog(dialogOrderDetails: IProcessedOrders) {
        goToNextStep(
            dialogKeys.ORDER,
            {
                ComponentProps: {
                    orderID: dialogOrderDetails.id,
                    setMessageOptions:setMessageOptions,
                    path:path,
                    url:generateTemplateURL(`${dialogOrderDetails.id}`),

                }
            }, `${path}/${dialogOrderDetails.id}`, null)
    }
    function renderItems() {
        if (orderKey === "openedOrders" &&
            selectedTab === OrderTabs.COLLECTION &&
            isPickupNow) {
            return <PickupListContainer collectionPoints={collectionPoints}
                emptyMessageObject={messageOps[orderKey]}
                orderList={selectedList} openOrderDialog={openOrderDialog} />;
        } else {
            return <>
                <div key={`columnHeaders-${orderKey}`} className={Styles.columnsHeader}>
                    {newOrderColumn.map((column) => <OrderListHeaders {...column} key={`col-${orderKey}-${column.key}-${column.label}`} />)}
                </div>
                <OrderListView orderList={selectedList} columns={newOrderColumn} openOrderDialog={openOrderDialog} emptyMessageObject={messageOps[orderKey]} />
            </>;
        }
    }
    return <Switch>
        <DisableRouteComponent pageKey={position}>
            <Route path={path}>
                <div>
                    <OrderActionBar orderKey={orderKey} filters={filterState} orderLists={orderItemList} selectedTab={selectedTab} isPickupNow={isPickupNow}
                        dispatch={dispatch} />
                    {renderItems()}
                </div>
                <MultiDialogRoute
                    RouteSteps={[
                        {
                            componentProps: {
                                dialogType: orderKey === "newOrders" ? DialogOrderTypes.NEW_ORDERS : DialogOrderTypes.OPEN_ORDERS,
                                collectionPoints: state.collectionPoints,
                                setMessageOptions,
                            },
                            key: dialogKeys.ORDER,
                            Component: OrderDetailsDialog,
                            path: `${path}${BY_ID}`,
                            title: t("orderHistory.modal.title"),
                            dialogClassName: detailsStyles.OrderHistoryPageDialog,
                            isFormDialog: false,
                            exact:true
                        },
                        {
                            key: dialogKeys.PARTIALREFUNDREASONS,
                            Component: IssueRefundDialog,
                            path: `${path}${BY_ID}${REFUND_REASONS_URL}`,
                            title: "PartialReafund.OrderHistory.Void.DialogTitle",
                            exact:true
                        },
                        {
                            key: dialogKeys.SPECIFICITEMSREFUND,
                            Component: OrderDetailsChecklistDialog,
                            path: `${path}${BY_ID}${SPECIFIC_ITEMS_REFUND_URL}`,
                            title: "PartialReafund.NewOpenOrders.SpecificItemsDialogTitle",
                            dialogClassName:detailsStyles.ModalContainer,
                            exact:true
                        },
                    ]}
                    navigateBackRotue={path}
                />
            </Route>

        </DisableRouteComponent>
    </Switch>
}

const messageOps = {
    "newOrders": {
        title: "order.emptyList.newOrders",
        info: "order.emptyList.newOrdersInfo",
    },
    "openedOrders": {
        title: "order.emptyList.openOrders",
        info: "order.emptyList.openOrderInfo",
    }
}